"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.client = exports.defaultKanbanViewStatus = exports.defaultOrgChartViewStatus = exports.defaultCalendarViewStatus = exports.defaultGanttViewStatus = exports.defaultWidgetPanelStatus = void 0;
const immer_1 = __importDefault(require("immer"));
const lodash_1 = require("lodash");
const redux_1 = require("redux");
const action_constants_1 = require("../../../../../shared/store/action_constants");
const constants_1 = require("../../../../../shared/store/constants");
const collaborators_1 = require("./collaborators");
const grid_view_active_field_1 = require("./grid_view_active_field");
const grid_view_drag_1 = require("./grid_view_drag");
const selection_1 = require("./selection");
const defaultActiveRowInfo = {
    type: constants_1.WhyRecordMoveType.UpdateRecord,
    positionInfo: {
        fieldId: '',
        recordId: '',
        visibleRowIndex: 0,
        isInit: true,
    },
    recordSnapshot: { meta: { fieldMap: {} }, recordMap: {} },
};
const MIN_WIDGET_PANEL_WIDTH = 320;
exports.defaultWidgetPanelStatus = {
    opening: false,
    width: MIN_WIDGET_PANEL_WIDTH,
    activePanelId: null,
    loading: false,
};
exports.defaultGanttViewStatus = {
    gridWidth: 256,
    gridVisible: true,
    settingPanelWidth: MIN_WIDGET_PANEL_WIDTH,
    settingPanelVisible: true,
    dateUnitType: constants_1.DateUnitType.Month,
};
exports.defaultCalendarViewStatus = {
    // whether the first guide is completed
    guideStatus: false,
    gridWidth: 256,
    gridVisible: false,
    settingPanelVisible: true,
    settingPanelWidth: MIN_WIDGET_PANEL_WIDTH,
};
exports.defaultOrgChartViewStatus = {
    guideStatus: false,
    rightPanelWidth: 256,
    rightPanelVisible: true,
    settingPanelVisible: false,
    settingPanelWidth: MIN_WIDGET_PANEL_WIDTH,
};
exports.defaultKanbanViewStatus = {
    groupSettingVisible: false,
};
exports.client = (0, redux_1.combineReducers)({
    gridViewDragState: grid_view_drag_1.gridViewDragState,
    gridViewActiveFieldState: grid_view_active_field_1.gridViewActiveFieldState,
    selection: selection_1.selection,
    collaborators: collaborators_1.collaborators,
    newRecordExpectIndex: (state = null, action) => {
        if (action.type === action_constants_1.SET_NEW_RECORD_EXPECT_INDEX) {
            return action.payload;
        }
        return state;
    },
    activeRowInfo: (state = defaultActiveRowInfo, action) => {
        if (action.type === action_constants_1.SET_ACTIVE_ROW_INFO) {
            return action.payload;
        }
        if (action.type === action_constants_1.CLEAR_ACTIVE_ROW_INFO) {
            return null;
        }
        return state;
    },
    searchResultCursorIndex: (state = 0, action) => {
        if (action.type === action_constants_1.SET_SEARCH_RESULT_CURSOR_INDEX) {
            return action.payload;
        }
        return state;
    },
    searchKeyword: (state = '', action) => {
        if (action.type === action_constants_1.SET_SEARCH_KEYWORD) {
            return action.payload;
        }
        return state;
    },
    groupingCollapseIds: (state = [], action) => {
        if (action.type === action_constants_1.SET_GROUPING_COLLAPSE) {
            return action.payload;
        }
        return state;
    },
    kanbanGroupCollapse: (state = [], action) => {
        if (action.type === action_constants_1.SET_KANBAN_GROUPING_EXPAND) {
            return action.payload;
        }
        return state;
    },
    isEditingCell: (state = null, action) => {
        if (action.type === action_constants_1.SET_EDIT_STATUS) {
            return action.payload;
        }
        return state;
    },
    loadingRecord: (0, immer_1.default)((state = {}, action) => {
        if (action.type === action_constants_1.SET_LOADING_RECORD) {
            const { recordIds, loading } = action.payload;
            recordIds.forEach(recordId => {
                state[recordId] = loading;
            });
        }
        return state;
    }, {}),
    widgetPanelStatus: (state = exports.defaultWidgetPanelStatus, action) => {
        switch (action.type) {
            case action_constants_1.TOGGLE_WIDGET_PANEL: {
                return Object.assign(Object.assign({}, state), { opening: action.payload == null ? !state.opening : action.payload });
            }
            case action_constants_1.CHANGE_WIDGET_PANEL_WIDTH: {
                return Object.assign(Object.assign({}, state), { width: Math.max(action.payload, MIN_WIDGET_PANEL_WIDTH) });
            }
            case action_constants_1.SWITCH_ACTIVE_PANEL: {
                return Object.assign(Object.assign({}, state), { activePanelId: action.payload });
            }
            case action_constants_1.SET_WIDGET_PANEL_LOADING: {
                return Object.assign(Object.assign({}, state), { loading: action.payload });
            }
            default: {
                return state;
            }
        }
    },
    ganttViewStatus: (state = exports.defaultGanttViewStatus, action) => {
        switch (action.type) {
            case action_constants_1.TOGGLE_GANTT_GRID: {
                return Object.assign(Object.assign({}, state), { gridVisible: action.payload == null ? !state.gridVisible : action.payload });
            }
            case action_constants_1.SET_GANTT_GRID_WIDTH: {
                return Object.assign(Object.assign({}, state), { gridWidth: action.payload });
            }
            case action_constants_1.TOGGLE_GANTT_SETTING_PANEL: {
                return Object.assign(Object.assign({}, state), { settingPanelVisible: action.payload == null ? !state.settingPanelVisible : action.payload });
            }
            case action_constants_1.SET_GANTT_SETTING_PANEL_WIDTH: {
                return Object.assign(Object.assign({}, state), { settingPanelWidth: action.payload });
            }
            case action_constants_1.SET_GANTT_DATE_UNIT_TYPE: {
                return Object.assign(Object.assign({}, state), { dateUnitType: action.payload });
            }
            default: {
                return state;
            }
        }
    },
    calendarViewStatus: (state = exports.defaultCalendarViewStatus, action) => {
        switch (action.type) {
            case action_constants_1.TOGGLE_CALENDAR_GUIDE_STATUS: {
                return Object.assign(Object.assign({}, state), { guideStatus: action.payload == null ? !state.guideStatus : action.payload });
            }
            case action_constants_1.TOGGLE_CALENDAR_GRID: {
                return Object.assign(Object.assign({}, state), { gridVisible: action.payload == null ? !state.gridVisible : action.payload });
            }
            case action_constants_1.SET_CALENDAR_GRID_WIDTH: {
                return Object.assign(Object.assign({}, state), { gridWidth: action.payload });
            }
            case action_constants_1.TOGGLE_CALENDAR_SETTING_PANEL: {
                return Object.assign(Object.assign({}, state), { settingPanelVisible: action.payload == null ? !state.settingPanelVisible : action.payload });
            }
            case action_constants_1.SET_CALENDAR_SETTING_PANEL_WIDTH: {
                return Object.assign(Object.assign({}, state), { settingPanelWidth: action.payload });
            }
            default: {
                return state;
            }
        }
    },
    orgChartViewStatus: (state = exports.defaultOrgChartViewStatus, action) => {
        switch (action.type) {
            case action_constants_1.TOGGLE_ORG_CHART_GUIDE_STATUS: {
                return Object.assign(Object.assign({}, state), { guideStatus: action.payload == null ? !state.guideStatus : action.payload });
            }
            case action_constants_1.TOGGLE_ORG_CHART_GRID: {
                return Object.assign(Object.assign({}, state), { rightPanelVisible: action.payload == null ? !state.rightPanelVisible : action.payload });
            }
            case action_constants_1.SET_ORG_CHART_GRID_WIDTH: {
                return Object.assign(Object.assign({}, state), { rightPanelWidth: action.payload });
            }
            case action_constants_1.TOGGLE_ORG_CHART_SETTING_PANEL: {
                return Object.assign(Object.assign({}, state), { settingPanelVisible: action.payload == null ? !state.settingPanelVisible : action.payload });
            }
            case action_constants_1.SET_ORG_CHART_SETTING_PANEL_WIDTH: {
                return Object.assign(Object.assign({}, state), { settingPanelWidth: action.payload });
            }
            default: {
                return state;
            }
        }
    },
    kanbanViewStatus: (state = exports.defaultKanbanViewStatus, action) => {
        switch (action.type) {
            case action_constants_1.TOGGLE_KANBAN_GROUP_SETTING_VISIBLE: {
                return Object.assign(Object.assign({}, state), { groupSettingVisible: action.payload == null ? !state.groupSettingVisible : action.payload });
            }
        }
        return state;
    },
    gridViewHoverFieldId(state = null, action) {
        switch (action.type) {
            case action_constants_1.SET_GRID_VIEW_HOVER_FIELD_ID: {
                return action.payload;
            }
        }
        return state;
    },
    highlightFiledId(state = null, action) {
        if (action.type === action_constants_1.SET_HIGHLIGHT_FIELD_ID) {
            return action.payload;
        }
        return state;
    },
    closeSyncViewIds(state = [], action) {
        switch (action.type) {
            case action_constants_1.SET_CLOSE_SYNC_VIEW_ID: {
                if (!state) {
                    return [action.payload];
                }
                if (state.includes(action.payload)) {
                    return state.filter(item => item !== action.payload);
                }
                return [...state, action.payload];
            }
        }
        return state;
    },
    isRobotPanelOpen(state = false, action) {
        if (action.type === action_constants_1.SET_ROBOT_PANEL_STATUS) {
            return action.payload;
        }
        return state;
    },
    operateViewIds(state = null, action) {
        const viewId = action.payload;
        if (action.type === action_constants_1.ACTIVE_OPERATE_VIEW_ID) {
            if (!state) {
                return [viewId];
            }
            return [...state, viewId];
        }
        if (action.type === action_constants_1.RESET_OPERATE_VIEW_ID) {
            if (!state) {
                return state;
            }
            return state.filter(item => item !== viewId);
        }
        return state;
    },
    isTimeMachinePanelOpen(state = false, action) {
        if (action.type === action_constants_1.TOGGLE_TIME_MACHINE_PANEL) {
            if (action.payload != null) {
                return action.payload;
            }
            return !state;
        }
        return state;
    },
    exportViewId(state = null, action) {
        if (action.type === action_constants_1.ACTIVE_EXPORT_VIEW_ID) {
            const viewId = action.payload;
            return viewId;
        }
        if (action.type === action_constants_1.RESET_EXPORT_VIEW_ID) {
            return null;
        }
        return state;
    },
    viewDerivation(state = {}, action) {
        if (action.type === action_constants_1.SET_VIEW_DERIVATION) {
            return Object.assign(Object.assign({}, state), { [action.payload.viewId]: action.payload.viewDerivation });
        }
        if (action.type === action_constants_1.PATCH_VIEW_DERIVATION) {
            const oldState = state[action.payload.viewId];
            if (oldState) {
                return Object.assign(Object.assign({}, state), { [action.payload.viewId]: Object.assign(Object.assign({}, oldState), action.payload.viewDerivation) });
            }
            return state;
        }
        if (action.type === action_constants_1.DELETE_VIEW_DERIVATION) {
            return (0, lodash_1.omit)(state, [action.payload.viewId]);
        }
        return state;
    },
});
//# sourceMappingURL=client.js.map